/**
 * @fileoverview Page for document verification via hash or QR code
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LandingNavbar } from '@/components/layout/LandingNavbar';
import { Footer } from '@/components/layout/Footer';
import { verificationService } from '@/services/documents/verificationService';
import { formatDate } from '@/utils/formatters';
import { useLanguage } from '@/contexts/LanguageContext';
export const VerificationPage = () => {
  const { t } = useLanguage();
  const [verificationMode, setVerificationMode] = useState('hash');
  const [hashInput, setHashInput] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [qrScanning, setQrScanning] = useState(false);
  const navigate = useNavigate();
  const handleHashSubmit = async (e) => {
    e.preventDefault();
    if (!hashInput.trim()) {
      alert(t('verify.enter_hash', 'Por favor, ingrese un hash o ID de documento'));
      return;
    }
    setIsVerifying(true);
    try {
      const result = await verificationService.verifyDocument(hashInput.trim());
      setVerificationResult(result);
    }
    catch (error) {
      alert(t('verify.verify_error', `Error al verificar: ${error.message}`));
    }
    finally {
      setIsVerifying(false);
    }
  };
  const handleQRScan = () => {
    setQrScanning(true);
    // In a real implementation, this would open camera for QR scanning
    // For now, we'll use a prompt as placeholder
    const qrData = prompt(t('verify.scan_prompt', 'Por favor, escanee el código QR o ingrese el hash manualmente:'));
    setQrScanning(false);
    if (qrData) {
      const extractedHash = verificationService.extractHashFromQR(qrData);
      if (extractedHash) {
        setHashInput(extractedHash);
        setVerificationMode('hash');
        // Auto-verify
        setTimeout(() => {
          handleHashSubmit(new Event('submit'));
        }, 100);
      }
      else {
        alert(t('verify.qr_extract_error', 'No se pudo extraer el hash del código QR'));
      }
    }
  };
  const resetVerification = () => {
    setVerificationResult(null);
    setHashInput('');
  };
  return (<div className="verification-page">
    <LandingNavbar />

    <div className="verification-container">
      <div className="verification-header">
        <h1>
          <i className="fas fa-shield-check"></i>
          {t('verify.title', 'Verificación de Documentos')}
        </h1>
        <p>{t('verify.subtitle', 'Verifica la autenticidad e integridad de tus documentos certificados')}</p>
      </div>

      {!verificationResult ? (<div className="verification-form-container">
        {/* Mode Toggle */}
        <div className="verification-mode-toggle">
          <button type="button" className={`mode-btn ${verificationMode === 'hash' ? 'active' : ''}`} onClick={() => {
            setVerificationMode('hash');
            resetVerification();
          }}>
            <i className="fas fa-key"></i>
            {t('verify.input_hash', 'Ingresar Hash')}
          </button>
          <button type="button" className={`mode-btn ${verificationMode === 'qr' ? 'active' : ''}`} onClick={() => {
            setVerificationMode('qr');
            resetVerification();
          }}>
            <i className="fas fa-qrcode"></i>
            {t('verify.scan_qr', 'Escanear QR')}
          </button>
        </div>

        {/* Hash Input Mode */}
        {verificationMode === 'hash' && (<form onSubmit={handleHashSubmit} className="verification-form">
          <div className="form-group">
            <div className="label-container">
              <p className="label">{t('verify.hash_id_label', 'Hash o ID del Documento')}</p>
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('verify.hash_placeholder', 'Ingrese el hash blockchain o ID del documento')}
                  value={hashInput}
                  onChange={(e) => setHashInput(e.target.value)}
                  disabled={isVerifying}
                />
                <i className="fas fa-search input-icon"></i>
              </div>
              <p className="hint">{t('verify.hint', 'Puede ingresar el hash blockchain o el ID del documento')}</p>

              <button
                className={`verify-btn ${isVerifying ? 'loading' : ''}`}
                onClick={handleHashSubmit}
                disabled={isVerifying || !hashInput.trim()}
              >
                {isVerifying ? (
                  <><i className="fas fa-circle-notch fa-spin"></i> {t('verify.verifying', 'Verificando...')}</>
                ) : (
                  <>{t('verify.btn_verify', 'Verificar Documento')}</>
                )}
              </button>
            </div>
          </div>
        </form>)}

        {verificationMode === 'qr' && (
          <div className="qr-section">
            <div className={`qr-placeholder ${isVerifying ? 'scanning' : ''}`}>
              <i className="fas fa-qrcode"></i>
              {isVerifying && <div className="scanner-line"></div>}
            </div>
            <h3>{t('verify.scan_qr_cam', 'Escanear Código QR')}</h3>
            <p>{t('verify.scan_qr_desc', 'Haz clic en el botón para escanear el código QR del documento')}</p>

            <button
              className={`verify-btn ${isVerifying ? 'loading' : ''}`}
              onClick={handleQRScan}
              disabled={isVerifying}
            >
              {isVerifying ? (
                <><i className="fas fa-circle-notch fa-spin"></i> {t('verify.verifying', 'Verificando...')}</>
              ) : (
                <><i className="fas fa-camera"></i> {t('verify.scan_qr', 'Escanear QR')}</>
              )}
            </button>
          </div>
        )}
      </div>) : (<div className="verification-results">
        <div className="results-container">
          <div className="results-header">
            <div className="status-badge valid">
              <i className="fas fa-check-circle"></i>
              {t('verify.valid_msg', 'Documento Válido')}
            </div>
            <h2>{t('verify.results_title', 'Resultados de Verificación')}</h2>
            <p>{t('verify.valid_desc', 'El documento ha sido verificado correctamente')}</p>
          </div>

          <div className="results-grid">
            <div className="result-card">
              <div className="result-icon">
                <i className="fas fa-file-search"></i>
              </div>
              <div className="result-info">
                <h3>{t('verify.doc_found', 'Documento Encontrado')}</h3>
                <p>{t('verify.doc_exists', 'El documento existe en el sistema')}</p>
              </div>
              <div className="result-status success">
                <i className="fas fa-check"></i>
              </div>
            </div>

            <div className="result-card">
              <div className="result-icon">
                <i className="fab fa-ethereum"></i>
              </div>
              <div className="result-info">
                <h3>{t('verify.blockchain_check', 'Verificación Blockchain')}</h3>
                <p>Hash: {verificationResult.tx_hash?.substring(0, 10)}...</p>
              </div>
              <div className="result-status success">
                <i className="fas fa-check"></i>
              </div>
            </div>

            <div className="result-card">
              <div className="result-icon">
                <i className="fas fa-university"></i>
              </div>
              <div className="result-info">
                <h3>{t('verify.issuer_check', 'Emisor Original')}</h3>
                <p>{verificationResult.issuer_name}</p>
              </div>
              <div className="result-status success">
                <i className="fas fa-check"></i>
              </div>
            </div>

            <div className="result-card">
              <div className="result-icon">
                <i className="fas fa-fingerprint"></i>
              </div>
              <div className="result-info">
                <h3>{t('verify.integrity_check', 'Integridad')}</h3>
                <p>{t('common.verified', 'Verificado')}</p>
              </div>
              <div className="result-status success">
                <i className="fas fa-check"></i>
              </div>
            </div>
          </div>

          <div className="document-details">
            <h3>{t('verify.doc_details', 'Detalles del Documento')}</h3>
            <div className="details-table">
              <div className="detail-row">
                <span className="detail-label">{t('issue.recipient_name', 'Destinatario')}</span>
                <span className="detail-value">{verificationResult.recipient_name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('landing.issue_date', 'Fecha Emisión')}</span>
                <span className="detail-value">{formatDate(verificationResult.issued_at)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">ID</span>
                <span className="detail-value font-mono">{verificationResult.document_id}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('verify.status', 'Estado')}</span>
                <span className="detail-value status-tag">{t('verify.status_issued', 'Emitido')}</span>
              </div>
            </div>
          </div>

          <button className="verify-again-btn" onClick={resetVerification}>
            <i className="fas fa-undo"></i>
            {t('verify.btn_again', 'Verificar Otro Documento')}
          </button>
        </div>
      </div>)}
    </div>

    <Footer />
  </div>);
};
